import React, { useState, useRef, useEffect } from "react";
import { supabase, STORAGE_BUCKET } from "../../supabase.config";
import { v4 as uuidv4 } from 'uuid';

export default function AddStudySpotPage() {
  const [name, setName] = useState("");
  const [building, setBuilding] = useState("");
  const [buildingId, setBuildingId] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [spaceType, setSpaceType] = useState("");
  const [location, setLocation] = useState("");
  const [features, setFeatures] = useState([]);
  const [noiseLevel, setNoiseLevel] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const [capacity, setCapacity] = useState("");
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Fetch buildings from Supabase on mount
  useEffect(() => {
    const fetchBuildings = async () => {
      const { data, error } = await supabase
        .from('Building')
        .select('id, name')
        .order('name');
      
      if (!error && data) {
        setBuildings(data);
      }
    };
    fetchBuildings();
  }, []);

  const uploadImages = async (files, studySpotId) => {
    const photoRecords = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExt = file.name.split('.').pop();
      const fileName = `${i}.${fileExt}`;
      const storagePath = `universities/wlu/photos/study-spots/${studySpotId}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (uploadError) {
        console.error('Upload error:', uploadError);
        continue;
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(storagePath);
      
      photoRecords.push({
        id: uuidv4(),
        storagePath,
        url: urlData.publicUrl,
        studySpotId,
        uploadedAt: new Date().toISOString()
      });
    }
    
    return photoRecords;
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const processFiles = (fileList) => {
    const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const imageFiles = Array.from(fileList).filter(file => 
      validImageTypes.includes(file.type)
    );

    setFiles(prevFiles => [...prevFiles, ...imageFiles]);
    
    imageFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    processFiles(droppedFiles);
  };

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    processFiles(selectedFiles);
  };

  const handleDeleteImage = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setPreviewUrls(prevUrls => prevUrls.filter((_, i) => i !== index));
  };

  const handleFeatureChange = (value) => {
    setFeatures(prev => 
      prev.includes(value)
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const handleBuildingChange = (selectedName) => {
    setBuilding(selectedName);
    const selectedBuilding = buildings.find(b => b.name === selectedName);
    setBuildingId(selectedBuilding?.id || '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const studySpotId = uuidv4();
      
      // Upload images first
      const photoRecords = await uploadImages(files, studySpotId);
      
      // Create study spot record
      const { error: spotError } = await supabase
        .from('StudySpot')
        .insert({
          id: studySpotId,
          name,
          buildingId,
          location,
          description: '', // Add description field if needed
          features,
          noiseLevel,
          spaceType
        });

      if (spotError) throw spotError;

      // Insert photo records
      if (photoRecords.length > 0) {
        const { error: photoError } = await supabase
          .from('Photo')
          .insert(photoRecords);
        
        if (photoError) {
          console.error('Error saving photo records:', photoError);
        }
      }

      // Reset form
      setName("");
      setBuilding("");
      setBuildingId("");
      setSpaceType("");
      setLocation("");
      setFiles([]);
      setPreviewUrls([]);
      setFeatures([]);
      setNoiseLevel("");
      setCapacity("");

      alert("Study spot submitted successfully!");
    } catch (error) {
      console.error('Error submitting study spot:', error);
      alert("Error submitting study spot. Please try again.");
    }

    setIsSubmitting(false);
  };

  const formStyles = {
    container: {
      maxWidth: "800px",
      margin: "2rem auto",
      padding: "2rem 30px",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    title: {
      fontSize: "24px",
      marginBottom: "25px",
      color: "#333"
    },
    section: {
      marginBottom: "25px",
      padding: "20px",
      backgroundColor: "white",
      borderRadius: "6px",
      border: "1px solid #e0e0e0"
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "500",
      color: "#444"
    },
    input: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      marginBottom: "8px",
      color: "#333"
    },
    optionContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gap: "10px"
    },
    option: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: "#333"
    },
    button: {
      padding: "12px 24px",
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "16px",
      transition: "background-color 0.2s"
    },
    buttonDisabled: {
      backgroundColor: "#cccccc",
      cursor: "not-allowed"
    },
    imagePreviewContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '10px'
    },
    imagePreview: {
      width: '100px',
      height: '100px',
      objectFit: 'cover',
      borderRadius: '4px'
    },
    imagePreviewWrapper: {
      position: 'relative'
    },
    deleteButton: {
      position: 'absolute',
      top: '-8px',
      right: '-8px',
      background: 'red',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px'
    },
    fileInput: {
      display: 'none'
    },
    fileInputLabel: {
      display: 'inline-block',
      padding: '8px 12px',
      backgroundColor: '#e0e0e0',
      color: '#333',
      borderRadius: '4px',
      cursor: 'pointer',
      marginBottom: '10px',
      transition: 'background-color 0.2s'
    },
    dropZone: {
      border: '2px dashed #ccc',
      borderRadius: '4px',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#fafafa',
      cursor: 'pointer',
      marginBottom: '10px',
      transition: 'all 0.3s ease'
    },
    dropZoneActive: {
      borderColor: '#007bff',
      backgroundColor: '#e3f2fd'
    },
    dropZoneText: {
      color: '#666',
      marginBottom: '10px'
    }
  };

  // Get building names for the form options
  const buildingNames = buildings.map(b => b.name);

  return (
    <div style={formStyles.container}>
      <h1 style={formStyles.title}>Add Study Spot</h1>
      
      <form onSubmit={handleSubmit}>
        <div style={formStyles.section}>
          <label style={formStyles.label}>Study Spot Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Quiet Corner in Library"
            style={formStyles.input}
          />
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Building *</label>
          <div style={formStyles.optionContainer}>
            {buildingNames.map((option) => (
              <div key={option} style={formStyles.option}>
                <input
                  type="radio"
                  name="building"
                  value={option}
                  checked={building === option}
                  onChange={(e) => handleBuildingChange(e.target.value)}
                  required
                />
                <label>{option}</label>
              </div>
            ))}
          </div>
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Space Type *</label>
          <div style={formStyles.optionContainer}>
            {["Group Study Room", "Independent Study Room", "Media Lab / Studio",
              "Meeting and Presentation Space", "Open Space", "Study Carrel"
            ].map((option) => (
              <div key={option} style={formStyles.option}>
                <input
                  type="radio"
                  name="spaceType"
                  value={option}
                  checked={spaceType === option}
                  onChange={(e) => setSpaceType(e.target.value)}
                  required
                />
                <label>{option}</label>
              </div>
            ))}
          </div>
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Location (room #)</label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={formStyles.input}
            placeholder="e.g., LH2019"
          />
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Capacity (number of people)</label>
          <input
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            min="1"
            max="100"
            style={formStyles.input}
            placeholder="e.g., 4"
          />
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Images</label>
          <div
            style={{
              ...formStyles.dropZone,
              ...(isDragging ? formStyles.dropZoneActive : {})
            }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <p style={formStyles.dropZoneText}>
              Drag and drop images here, or click to select files
            </p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              multiple
              accept="image/*"
              style={formStyles.fileInput}
            />
          </div>
          
          <div style={formStyles.imagePreviewContainer}>
            {previewUrls.map((url, index) => (
              <div key={index} style={formStyles.imagePreviewWrapper}>
                <img
                  src={url}
                  alt={`Preview ${index + 1}`}
                  style={formStyles.imagePreview}
                />
                <button
                  type="button"
                  onClick={() => handleDeleteImage(index)}
                  style={formStyles.deleteButton}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Features</label>
          <div style={formStyles.optionContainer}>
            {["Wifi", "Power Outlets", "Whiteboard", "Cafe", 
              "Couches", "FoodAndDrinks", "Private Desks", "Accessible"
            ].map((feature) => (
              <div key={feature} style={formStyles.option}>
                <input
                  type="checkbox"
                  checked={features.includes(feature)}
                  onChange={() => handleFeatureChange(feature)}
                />
                <label>{feature}</label>
              </div>
            ))}
          </div>
        </div>

        <div style={formStyles.section}>
          <label style={formStyles.label}>Noise Level *</label>
          <div style={formStyles.optionContainer}>
            {["quiet", "moderate", "busy"].map((level) => (
              <div key={level} style={formStyles.option}>
                <input
                  type="radio"
                  name="noiseLevel"
                  value={level}
                  checked={noiseLevel === level}
                  onChange={(e) => setNoiseLevel(e.target.value)}
                  required
                />
                <label style={{textTransform: 'capitalize'}}>{level}</label>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            ...formStyles.button,
            ...(isSubmitting ? formStyles.buttonDisabled : {})
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
}
